import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { PRINT_SIZES_MM, type PrintSize } from '../lib/place-flyer-content'

interface PrintJob {
  size: PrintSize
  sheet: ReactNode
}

/** Prints one flyer sheet at a paper size; `portal` must be rendered for the sheet to exist. */
export function useFlyerPrint() {
  const [job, setJob] = useState<PrintJob | null>(null)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!job) return
    let cancelled = false
    const clear = () => setJob(null)
    window.addEventListener('afterprint', clear, { once: true })
    const images = Array.from(sheetRef.current?.querySelectorAll('img') ?? [])
    Promise.all(images.map((image) => image.decode().catch(() => undefined))).then(() => {
      if (!cancelled) window.print()
    })
    return () => {
      cancelled = true
      window.removeEventListener('afterprint', clear)
    }
  }, [job])

  const print = useCallback((size: PrintSize, sheet: ReactNode) => setJob({ size, sheet }), [])

  const paper = job ? PRINT_SIZES_MM[job.size] : null
  const portal =
    job && paper
      ? createPortal(
          <div ref={sheetRef} data-print-sheet className="hidden print:block">
            <style>{`@page { size: ${paper.width}mm ${paper.height}mm; margin: 0; }`}</style>
            <div
              className="break-after-avoid"
              style={{ width: `${paper.width}mm`, height: `${paper.height}mm` }}
            >
              {job.sheet}
            </div>
          </div>,
          document.body,
        )
      : null

  return { print, portal }
}
